import * as readline from 'readline';
import kleur from "kleur";
import fs from "fs";
import path from "path";
import { runAgent } from "./commands/run";
import { runInit } from "./commands/init";
import { runStatus } from "./commands/status";
import { runConfig } from "./commands/config";
import { runAuth } from "./commands/auth";
import { ResponseFormatter } from "./responseFormatter";
import { getSessionMemory } from "../runtime/sessionMemory";
import { getSessionPersistence } from "../runtime/sessionPersistence";
import { ToolCallingAgent } from "../agent/toolCallAgent";

// Thinking terms for flavor (like Claude Code)
const THINKING_TERMS = [
  'Thinking',
  'Analyzing', 
  'Processing',
  'Contemplating',
  'Examining',
  'Exploring',
  'Understanding',
  'Evaluating',
  'Considering',
  'Pondering',
  'Reasoning',
  'Reflecting'
];

export class InteractiveSession {
  private rl: readline.Interface;
  private isSessionActive = true;
  private thinkingInterval?: NodeJS.Timeout;
  private currentThinkingIndex = 0;
  private sessionId: string;
  private agent: ToolCallingAgent;
  private sessionMemory = getSessionMemory();
  private sessionPersistence = getSessionPersistence();

  constructor() {
    this.sessionId = `interactive-${Date.now()}`;
    
    this.rl = readline.createInterface({
      input: process.stdin,
      output: process.stdout,
      prompt: this.createPrompt()
    });
    
    this.agent = new ToolCallingAgent(this.sessionId, this.rl);
    this.sessionPersistence.setReadlineInterface(this.rl);
  }

  private createPrompt(): string {
    const modeDisplay = this.agent ? this.agent.getPermissionModeDisplay() : kleur.gray('[normal]');
    const promptContent = kleur.cyan('metis') + kleur.gray(' ') + modeDisplay + kleur.gray('> ');
    const border = kleur.gray('| ');
    return `${border}${promptContent}`;
  }

  private showInputContainer() {
    const width = process.stdout.columns || 80;
    const topBorder = kleur.gray('+' + '-'.repeat(width - 2) + '+');
    const bottomBorder = kleur.gray('+' + '-'.repeat(width - 2) + '+');
    
    console.log(topBorder);
    // The actual input line will be here
    return bottomBorder;
  }

  async start() {
    // Set interactive mode flag to prevent process.exit() calls
    process.env.METIS_INTERACTIVE = 'true';
    
    try {
      // Initialize session with enhanced persistence and recovery
      const initialization = await this.sessionPersistence.initializeSession(this.sessionId, {
        restorePermissions: true,
        resumeLastTask: true,
        showRecoveryPrompt: false  // Temporarily disable to test
      });
      
      const session = initialization.session;
      
      // Show recovery info if session was recovered
      if (initialization.wasRecovered) {
        console.log(kleur.green("🔄 Session recovered successfully!"));
        console.log();
      }
      
      this.showWelcome(session);
      this.setupEventHandlers();
      this.prompt();
    
    } catch (error) {
      // Fallback - continue with basic session
      this.showWelcome();
      this.setupEventHandlers();
      this.prompt();
    }

    // Keep the session running
    return new Promise<void>((resolve) => {
      this.rl.on('close', () => {
        console.log(kleur.gray('\n👋 Goodbye!'));
        
        // Clean up permission system
        this.agent.cleanup();
        
        // Mark clean session exit
        this.sessionPersistence.markCleanExit();
        
        // Clean up old sessions on exit
        this.sessionMemory.cleanupOldSessions();
        
        resolve();
      });
    });
  }

  private showWelcome(session?: any) {
    console.log(kleur.cyan(`
███╗   ███╗███████╗████████╗██╗███████╗     ██████╗ ██████╗ ██████╗ ███████╗
████╗ ████║██╔════╝╚══██╔══╝██║██╔════╝    ██╔════╝██╔═══██╗██╔══██╗██╔════╝
██╔████╔██║█████╗     ██║   ██║███████╗    ██║     ██║   ██║██║  ██║█████╗  
██║╚██╔╝██║██╔══╝     ██║   ██║╚════██║    ██║     ██║   ██║██║  ██║██╔══╝  
██║ ╚═╝ ██║███████╗   ██║   ██║███████║    ╚██████╗╚██████╔╝██████╔╝███████╗
╚═╝     ╚═╝╚══════╝   ╚═╝   ╚═╝╚══════╝     ╚═════╝ ╚═════╝ ╚═════╝ ╚══════╝
`));

    const pkg = require("../../package.json");
    console.log(kleur.gray(`                    AI-Powered Development Assistant v${pkg.version}`));
    console.log();
    
    const welcomeBox = this.createBox([
      kleur.white("Welcome to Metis Code Interactive Session!"),
      "",
      kleur.gray("Type your coding requests in natural language:"),
      kleur.yellow('  "Fix the authentication bug in login.ts"'),
      kleur.yellow('  "Create a React component for user profiles"'),
      kleur.yellow('  "Add error handling to the API endpoints"'),
      "",
      kleur.white("Slash Commands:"),
      kleur.cyan("  /init") + kleur.gray("   - Create Agent.md with project instructions"),
      kleur.cyan("  /plan") + kleur.gray("   - Enter collaborative planning mode"),
      kleur.cyan("  /status") + kleur.gray(" - Show system status"), 
      kleur.cyan("  /config") + kleur.gray(" - Show configuration"),
      kleur.cyan("  /help") + kleur.gray("   - Show help"),
      kleur.cyan("  /exit") + kleur.gray("   - Exit session"),
      "",
      kleur.white("Permission Modes:"),
      kleur.gray("  Shift+Tab - Cycle permission modes"),
      kleur.gray("  Current: ") + this.agent.getPermissionModeDisplay()
    ], 'blue');
    
    console.log(welcomeBox);
    console.log();
    
    // Show session context if available
    if (session && session.currentTask) {
      const sessionBox = this.createBox([
        kleur.blue(`Session Context`),
        "",
        kleur.white(`Current Task: ${session.currentTask}`),
        session.workingFiles.length > 0 ? kleur.gray(`Working Files: ${session.workingFiles.slice(-3).join(", ")}`) : "",
        session.previousTasks.length > 0 ? kleur.gray(`Recent Tasks: ${session.previousTasks.slice(-2).join(", ")}`) : ""
      ].filter(Boolean), 'blue');
      
      console.log(sessionBox);
      console.log();
    }
    
    // Check if project is configured
    const hasConfig = this.checkConfiguration();
    if (!hasConfig) {
      console.log(kleur.yellow("Project not configured. Run ") + kleur.cyan("/init") + kleur.yellow(" to get started."));
      console.log();
    }
  }

  private checkConfiguration(): boolean {
    const cwd = process.cwd();
    const configPath = path.join(cwd, "metis.config.json");
    const secretsPath = path.join(cwd, ".metis", "secrets.json");
    
    if (!fs.existsSync(configPath)) return false;
    
    if (fs.existsSync(secretsPath)) {
      try {
        const secrets = JSON.parse(fs.readFileSync(secretsPath, 'utf8'));
        return Object.keys(secrets).length > 0;
      } catch {
        return false;
      }
    }
    
    return false;
  }

  private setupEventHandlers() {
    this.rl.on('line', async (input) => {
      const trimmedInput = input.trim();
      
      // Close the input container
      const width = process.stdout.columns || 80;
      const bottomBorder = kleur.gray('+' + '-'.repeat(width - 2) + '+');
      console.log(bottomBorder);
      console.log();
      
      if (!trimmedInput) {
        this.showPromptWithContainer();
        return;
      }

      try {
        await this.handleInput(trimmedInput);
      } catch (error) {
        this.formatError(`Error: ${error.message}`);
      }
      
      if (this.isSessionActive) {
        console.log();
        this.showPromptWithContainer();
      }
    });

    this.rl.on('SIGINT', () => {
      this.stopThinking();
      console.log(kleur.gray('\n👋 Use /exit to quit or Ctrl+C again to force exit'));
      this.showPromptWithContainer();
    });
  }

  private showPromptWithContainer() {
    // Show input container
    const width = process.stdout.columns || 80;
    const topBorder = kleur.gray('+' + '-'.repeat(width - 2) + '+');
    console.log(topBorder);
    this.rl.setPrompt(this.createPrompt());
    this.rl.prompt();
  }

  private async handleInput(input: string) {
    // Handle slash commands
    if (input.startsWith('/')) {
      await this.handleSlashCommand(input);
      return;
    }

    // Handle natural language requests
    await this.handleNaturalLanguage(input);
  }

  private async handleSlashCommand(command: string) {
    const [cmd, ...args] = command.slice(1).split(' ');
    
    switch (cmd.toLowerCase()) {
      case 'init':
        const initBox = this.createBox([
          kleur.blue('Initializing project...'),
          kleur.gray('Creating Agent.md with project instructions')
        ], 'blue');
        console.log(initBox);
        console.log();
        await runInit(['--agent-md']);
        break;

      case 'plan':
        await this.enterPlanningMode();
        break;
        
      case 'execute':
      case 'exec':
        await this.exitPlanningMode();
        break;
        
      case 'status':
        const statusBox = this.createBox([
          kleur.blue('System Status')
        ], 'blue');
        console.log(statusBox);
        console.log();
        await runStatus([]);
        break;
        
      case 'config':
        const configBox = this.createBox([
          kleur.blue('Configuration')
        ], 'blue');
        console.log(configBox);
        console.log();
        await runConfig(args.length > 0 ? args : ['show']);
        break;
        
      case 'help':
        this.showHelp();
        break;
        
      case 'exit':
      case 'quit':
      case 'q':
        const exitBox = this.createBox([
          kleur.green('Thank you for using Metis Code!'),
          kleur.gray('Session ended successfully')
        ], 'green');
        console.log(exitBox);
        this.isSessionActive = false;
        this.rl.close();
        break;
        
      case 'clear':
        this.handleClearCommand();
        break;

      case 'permissions':
      case 'perm':
        this.showPermissionInfo();
        break;

      case 'mode':
        if (args.length > 0) {
          const requestedMode = args[0].toLowerCase();
          try {
            const validModes = ['normal', 'auto_accept', 'plan_only'];
            if (validModes.includes(requestedMode)) {
              const mode = requestedMode as any;
              this.agent.setPermissionMode(mode);
              const modeDisplay = this.agent.getPermissionModeDisplay();
              console.log(kleur.green(`✅ Permission mode set to: ${modeDisplay}`));
              console.log(kleur.gray(this.agent.getPermissionManager().getModeDescription()));
            } else {
              this.formatError(`Invalid mode. Available: ${validModes.join(', ')}`);
            }
          } catch (error) {
            this.formatError(`Failed to set mode: ${error.message}`);
          }
        } else {
          const newMode = this.agent.cyclePermissionMode();
          const modeDisplay = this.agent.getPermissionModeDisplay();
          console.log(kleur.cyan(`🔄 Permission mode cycled to: ${modeDisplay}`));
          console.log(kleur.gray(this.agent.getPermissionManager().getModeDescription()));
        }
        break;

      case 'compact':
        await this.handleCompactCommand();
        break;

      case 'resume':
        await this.handleResumeCommand(args);
        break;

      case 'continue':
        await this.handleContinueCommand();
        break;

      case 'sessions':
        this.handleSessionsCommand();
        break;

      case 'approvals':
        this.handleApprovalsCommand();
        break;

      case 'memory':
      case 'agent':
        this.handleMemoryCommand(args);
        break;

      case 'reload':
      case 'refresh':
        this.handleReloadCommand();
        break;

      case 'git':
        this.handleGitCommand(args);
        break;

      case 'github':
      case 'gh':
        this.handleGithubCommand(args);
        break;
        
      default:
        this.formatError(`Unknown command: /${cmd}\nAvailable: /init, /plan, /execute, /status, /config, /help, /exit, /mode, /permissions, /clear, /compact, /resume, /continue, /sessions, /approvals, /memory, /reload, /git, /github`);
    }
  }

  private async handleNaturalLanguage(input: string) {
    this.startThinking();
    
    try {
      // Use the persistent agent with session memory
      const result = await this.agent.executeWithTools(input);
      
      this.stopThinking();
      
      if (result.type === 'completed') {
        const formattedResponse = ResponseFormatter.formatResponse(result.content);
        const responseBox = ResponseFormatter.createResponseBox(formattedResponse, '🤖 Metis Response');
        
        console.log(responseBox);
        
        // Show additional context if verbose
        if (process.env.METIS_VERBOSE === 'true' && result.iterations > 1) {
          console.log(kleur.gray(`\n📊 Completed in ${result.iterations} steps using ${result.toolCalls} operations`));
          if (result.tokens) {
            console.log(kleur.gray(`📊 ${result.tokens.total} tokens used`));
          }
        }
      } else if (result.type === 'failed') {
        this.formatError(`Task failed: ${result.content}`);
      } else if (result.type === 'max_iterations') {
        this.formatError('Task exceeded maximum iterations. Try breaking it into smaller steps.');
      }
      
    } catch (error: any) {
      this.stopThinking();
      this.formatError(`Failed to process request: ${error?.message || 'Unknown error'}`);
    }
  }

  private startThinking() {
    // Disable thinking animation to prevent interference with approval dialogs
    // Just show a simple static message
    console.log(kleur.blue('Processing...'));
  }

  private stopThinking() {
    // No need to clear anything since we're not using animation
    // Just ensure we have a clean line for the response
  }

  private formatError(message: string) {
    const errorBox = this.createBox([
      kleur.red('❌ Error'),
      '',
      kleur.white(message)
    ], 'red');
    console.log(errorBox);
  }

  private createBox(lines: string[], borderColor: 'red' | 'blue' | 'green' | 'gray' = 'gray'): string {
    const width = Math.max(...lines.map(line => this.stripAnsi(line).length)) + 4;
    const colorFn = borderColor === 'red' ? kleur.red : 
                   borderColor === 'blue' ? kleur.blue :
                   borderColor === 'green' ? kleur.green : kleur.gray;
    
    const topBorder = colorFn('+' + '-'.repeat(width - 2) + '+');
    const bottomBorder = colorFn('+' + '-'.repeat(width - 2) + '+');
    
    const boxedLines = lines.map(line => {
      const padding = width - this.stripAnsi(line).length - 3;
      return colorFn('| ') + line + ' '.repeat(padding) + colorFn('|');
    });
    
    return [topBorder, ...boxedLines, bottomBorder].join('\n');
  }

  private stripAnsi(str: string): string {
    return str.replace(/\u001b\[[0-9;]*m/g, '');
  }

  private handleClearCommand() {
    const clearBox = this.createBox([
      kleur.white("🔄 Clearing Session Context"),
      "",
      kleur.gray("This will clear the conversation history but keep:"),
      kleur.cyan("  • Session ID and basic info"),
      kleur.cyan("  • Permission settings"),
      kleur.cyan("  • Project configuration"),
      "",
      kleur.yellow("⚠️  Conversation history will be lost")
    ], 'yellow');
    
    console.log(clearBox);
    
    // Clear session memory
    this.sessionMemory.clearSession();
    
    // Clear screen and show fresh welcome
    console.clear();
    this.showWelcome();
    
    console.log(kleur.green("✅ Session context cleared successfully"));
  }

  private async handleCompactCommand() {
    const compactBox = this.createBox([
      kleur.white("🗜️  Compacting Session Context"),
      "",
      kleur.gray("This will summarize older messages to free up context space"),
      kleur.cyan("  • Keep last 3 messages"),
      kleur.cyan("  • Summarize older conversation"),
      kleur.cyan("  • Preserve important context")
    ], 'blue');
    
    console.log(compactBox);
    
    try {
      // Use the agent's provider for intelligent summarization
      const provider = (this.agent as any).provider;
      
      const intelligentSummarizer = async (messages: any[]) => {
        if (!provider || messages.length === 0) {
          return this.sessionMemory['defaultSummarizer'](messages);
        }
        
        try {
          const conversationText = messages.map(m => 
            `${m.role}: ${m.content}`
          ).join('\n\n');
          
          const summaryPrompt = `Please create a concise summary of this conversation, focusing on:
- Key tasks and requests
- Important decisions made  
- Files and components worked on
- Current progress and context

Conversation to summarize:
${conversationText}

Summary:`;

          const summary = await provider.send([
            { role: 'system', content: 'You are a helpful assistant that creates concise conversation summaries.' },
            { role: 'user', content: summaryPrompt }
          ]);
          
          return summary;
        } catch (error) {
          console.log(kleur.yellow('⚠️  Using simple summarizer due to error'));
          return this.sessionMemory['defaultSummarizer'](messages);
        }
      };
      
      await this.sessionMemory.compactSession(intelligentSummarizer);
      
      console.log(kleur.green("✅ Session context compacted successfully"));
      console.log(kleur.gray("   Older messages summarized, recent context preserved"));
      
    } catch (error) {
      console.log(kleur.red("❌ Failed to compact session:"), error.message);
    }
  }

  private async handleResumeCommand(args: string[]) {
    if (args.length === 0) {
      // Show available sessions
      this.handleSessionsCommand();
      console.log();
      console.log(kleur.white("💡 Usage: ") + kleur.cyan("/resume <session-id>"));
      return;
    }
    
    const sessionId = args[0];
    
    try {
      const resumedSession = await this.sessionPersistence.switchSession(sessionId);
      
      if (!resumedSession) {
        console.log(kleur.red("❌ Session not found: ") + sessionId);
        console.log(kleur.gray("   Use /sessions to see available sessions"));
        return;
      }
      
      // Update current session ID
      this.sessionId = sessionId;
      
      const resumeBox = this.createBox([
        kleur.white("📂 Enhanced Session Resumed"),
        "",
        kleur.white(`Session ID: ${resumedSession.sessionId}`),
        resumedSession.currentTask ? kleur.cyan(`Current Task: ${resumedSession.currentTask}`) : '',
        resumedSession.workingFiles.length ? kleur.gray(`Working Files: ${resumedSession.workingFiles.slice(-3).join(', ')}`) : '',
        kleur.gray(`Messages: ${resumedSession.messages.length}`),
        kleur.gray(`Last Activity: ${new Date(resumedSession.lastActivity).toLocaleString()}`),
        "",
        kleur.blue("✅ Permissions and approvals restored from previous session")
      ].filter(Boolean), 'green');
      
      console.log(resumeBox);
      
      // Update agent's session
      this.agent['context'].sessionId = resumedSession.sessionId;
      this.sessionId = resumedSession.sessionId;
      
    } catch (error) {
      console.log(kleur.red("❌ Failed to resume session:"), error.message);
    }
  }

  private async handleContinueCommand() {
    try {
      const lastSession = this.sessionMemory.getLastSession();
      
      if (!lastSession) {
        console.log(kleur.yellow("⚠️  No previous sessions found"));
        return;
      }
      
      if (lastSession.sessionId === this.sessionId) {
        console.log(kleur.yellow("⚠️  Already in the most recent session"));
        return;
      }
      
      // Resume the last session
      await this.handleResumeCommand([lastSession.sessionId]);
      
    } catch (error) {
      console.log(kleur.red("❌ Failed to continue last session:"), error.message);
    }
  }

  private handleSessionsCommand() {
    const sessions = this.sessionPersistence.getEnhancedSessionList(10);
    
    if (sessions.length === 0) {
      console.log(kleur.yellow("⚠️  No sessions found"));
      return;
    }
    
    const sessionLines = [kleur.white("📋 Enhanced Session History")];
    sessionLines.push("");
    
    // Add session statistics
    const stats = this.sessionPersistence.getSessionStats();
    sessionLines.push(kleur.gray(`Total sessions: ${stats.totalSessions} | Active approvals: ${stats.hasActiveApprovals ? 'Yes' : 'No'}`));
    if (stats.crashCount > 0) {
      sessionLines.push(kleur.yellow(`⚠️  Recent interruptions: ${stats.crashCount}`));
    }
    sessionLines.push("");
    
    sessions.forEach((session, index) => {
      const indicator = session.isActive ? kleur.green("●") : kleur.gray("○");
      const age = this.formatTimeAgo(session.lastActivity);
      
      sessionLines.push(
        `${indicator} ${kleur.cyan(session.sessionId.substring(0, 12))}... ${kleur.gray(age)}`
      );
      
      if (session.currentTask) {
        sessionLines.push(`   ${kleur.yellow(session.currentTask)}`);
      }
      
      // Enhanced session info
      const details = [];
      if (session.workingFiles > 0) details.push(`${session.workingFiles} files`);
      if (session.messageCount > 0) details.push(`${session.messageCount} msgs`);
      if (session.duration) details.push(`${session.duration}`);
      
      if (details.length > 0) {
        sessionLines.push(`   ${kleur.gray(details.join(" • "))}`);
      }
    });
    
    sessionLines.push("");
    sessionLines.push(kleur.white("Commands:"));
    sessionLines.push(kleur.cyan("  /resume <session-id>") + kleur.gray(" - Resume a specific session"));
    sessionLines.push(kleur.cyan("  /continue") + kleur.gray("           - Continue the last session"));
    sessionLines.push(kleur.cyan("  /approvals") + kleur.gray("         - View active session approvals"));
    
    const sessionBox = this.createBox(sessionLines, 'blue');
    console.log(sessionBox);
  }

  private handleApprovalsCommand() {
    const manager = this.agent.getPermissionManager();
    const approvals = manager.getSessionApprovals();
    
    if (approvals.length === 0) {
      const noApprovalsBox = this.createBox([
        kleur.white("📋 Session Approvals"),
        "",
        kleur.gray("No session approvals active"),
        "",
        kleur.white("💡 How it works:"),
        kleur.gray("When prompted for approval, choose 's/session' to approve"),
        kleur.gray("similar operations for the rest of this session.")
      ], 'blue');
      
      console.log(noApprovalsBox);
      return;
    }
    
    const approvalLines = [kleur.white("📋 Active Session Approvals")];
    approvalLines.push("");
    approvalLines.push(kleur.green(`✅ ${approvals.length} approval(s) active for this session:`));
    approvalLines.push("");
    
    approvals.forEach((approval, index) => {
      const parts = approval.split(':');
      if (parts[0] === 'tool') {
        approvalLines.push(`${index + 1}. ${kleur.cyan('Tool:')} ${kleur.yellow(parts[1])}`);
      } else if (parts[0] === 'category') {
        const category = parts[1];
        const riskLevel = parts[2] || 'unknown';
        approvalLines.push(`${index + 1}. ${kleur.cyan('Category:')} ${kleur.yellow(category)} ${kleur.gray(`(${riskLevel} risk)`)}`);
      } else {
        approvalLines.push(`${index + 1}. ${kleur.gray(approval)}`);
      }
    });
    
    approvalLines.push("");
    approvalLines.push(kleur.white("Commands:"));
    approvalLines.push(kleur.red("  /mode normal") + kleur.gray(" - Reset to normal approval mode"));
    approvalLines.push(kleur.gray("  Session approvals clear automatically when session ends"));
    
    const approvalsBox = this.createBox(approvalLines, 'green');
    console.log(approvalsBox);
  }

  private formatTimeAgo(timestamp: string): string {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = now.getTime() - time.getTime();
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'just now';
  }

  private showPermissionInfo() {
    const manager = this.agent.getPermissionManager();
    const currentMode = manager.getCurrentMode();
    const currentConfig = manager.getCurrentConfig();
    
    const permissionBox = this.createBox([
      kleur.white("🔒 Permission System"),
      "",
      kleur.white(`Current Mode: ${currentConfig.icon} ${currentMode.toUpperCase()}`),
      kleur.gray(currentConfig.description),
      "",
      kleur.white("Available Modes:"),
      kleur.green("  🔒 NORMAL    - Ask approval for sensitive operations"),
      kleur.yellow("  🚀 AUTO_ACCEPT - Execute operations without asking"),  
      kleur.blue("  📋 PLAN_ONLY  - Show plans but don't execute"),
      "",
      kleur.white("Commands:"),
      kleur.cyan("  /mode") + kleur.gray("        - Cycle through permission modes"),
      kleur.cyan("  /mode normal") + kleur.gray(" - Set specific mode"),
      kleur.gray("  Shift+Tab    - Quick mode cycling (in approval prompts)")
    ], 'blue');
    
    console.log(permissionBox);
  }

  private handleMemoryCommand(args: string[]) {
    const subCommand = args[0] || 'show';
    
    switch (subCommand.toLowerCase()) {
      case 'show':
      case 'status':
        this.showMemoryStatus();
        break;
        
      case 'agent':
      case 'agentmd':
        this.showAgentMdStatus();
        break;
        
      case 'compact':
        this.handleCompactCommand();
        break;
        
      default:
        this.formatError(`Unknown memory command: ${subCommand}\nAvailable: show, agent, compact`);
    }
  }

  private showMemoryStatus() {
    const agent = this.agent as any;
    const sessionMemory = agent.sessionMemory;
    const agentMemory = agent.agentMemory;
    
    // Get session info
    const currentSession = sessionMemory.getCurrentSession();
    const messageCount = currentSession.messages.length;
    const workingFiles = currentSession.workingFiles.length;
    const shouldCompact = sessionMemory.shouldAutoCompact();
    
    // Get project context
    const projectContext = agentMemory.generateProjectContext(true);
    const projectInstructions = agentMemory.getCurrentProjectInstructions();
    
    const memoryLines = [kleur.white("🧠 Memory System Status")];
    memoryLines.push("");
    
    // Session Memory
    memoryLines.push(kleur.blue("📝 Session Memory:"));
    memoryLines.push(`   Messages: ${messageCount} (${shouldCompact ? kleur.yellow('needs compaction') : kleur.green('healthy')})`);
    memoryLines.push(`   Working Files: ${workingFiles}`);
    memoryLines.push(`   Current Task: ${currentSession.currentTask || kleur.gray('none')}`);
    memoryLines.push("");
    
    // Project Memory (Agent.md)
    memoryLines.push(kleur.blue("🎯 Project Memory:"));
    if (projectInstructions.trim()) {
      const instructionLines = projectInstructions.split('\n').length;
      memoryLines.push(`   ${kleur.green('✅ Agent.md loaded')} (${instructionLines} lines)`);
    } else {
      memoryLines.push(`   ${kleur.gray('No Agent.md files found')}`);
    }
    memoryLines.push(`   ${projectContext}`);
    memoryLines.push("");
    
    // Memory Actions
    memoryLines.push(kleur.white("Available Actions:"));
    memoryLines.push(kleur.cyan("  /memory agent") + kleur.gray("   - View Agent.md file details"));
    memoryLines.push(kleur.cyan("  /memory compact") + kleur.gray(" - Compact session memory"));
    memoryLines.push(kleur.cyan("  /reload") + kleur.gray("         - Refresh Agent.md files"));
    memoryLines.push(kleur.cyan("  /clear") + kleur.gray("          - Clear session context"));
    
    const memoryBox = this.createBox(memoryLines, 'blue');
    console.log(memoryBox);
  }

  private showAgentMdStatus() {
    const agent = this.agent as any;
    const agentMemory = agent.agentMemory;
    
    // Force refresh and get hierarchy
    agentMemory.clearCache();
    const hierarchy = agentMemory.loadHierarchicalAgentMd();
    
    const agentMdLines = [kleur.white("🎯 Agent.md Hierarchy")];
    agentMdLines.push("");
    
    if (hierarchy.agentMdFiles.length === 0) {
      agentMdLines.push(kleur.gray("No Agent.md files found in project hierarchy"));
      agentMdLines.push("");
      agentMdLines.push(kleur.white("💡 To create an Agent.md file:"));
      agentMdLines.push(kleur.cyan("  /init") + kleur.gray(" - Generate intelligent Agent.md for this project"));
    } else {
      agentMdLines.push(kleur.green(`Found ${hierarchy.agentMdFiles.length} Agent.md file(s):`));
      agentMdLines.push("");
      
      hierarchy.agentMdFiles.forEach((agentMd, index) => {
        const relativePath = path.relative(process.cwd(), path.dirname(agentMd.filePath));
        const location = relativePath || 'project root';
        const fileName = path.basename(agentMd.filePath);
        const lines = agentMd.content.split('\n').length;
        const modified = agentMd.lastModified.toLocaleString();
        
        agentMdLines.push(`${index + 1}. ${kleur.cyan(fileName)} ${kleur.gray(`(${agentMd.level})`)}`);
        agentMdLines.push(`   ${kleur.gray(`Location: ${location}`)}`);
        agentMdLines.push(`   ${kleur.gray(`Size: ${lines} lines, Modified: ${modified}`)}`);
        agentMdLines.push("");
      });
      
      // Show a preview of the combined instructions
      const instructionPreview = hierarchy.projectInstructions.substring(0, 200);
      agentMdLines.push(kleur.white("📄 Combined Instructions Preview:"));
      agentMdLines.push(kleur.gray(instructionPreview + (hierarchy.projectInstructions.length > 200 ? '...' : '')));
    }
    
    const agentMdBox = this.createBox(agentMdLines, 'blue');
    console.log(agentMdBox);
  }

  private handleReloadCommand() {
    const agent = this.agent as any;
    const agentMemory = agent.agentMemory;
    
    // Force refresh Agent.md files
    agentMemory.clearCache();
    const hierarchy = agentMemory.loadHierarchicalAgentMd();
    
    const reloadBox = this.createBox([
      kleur.white("🔄 Agent.md Files Refreshed"),
      "",
      hierarchy.agentMdFiles.length > 0 
        ? kleur.green(`✅ Loaded ${hierarchy.agentMdFiles.length} Agent.md file(s)`)
        : kleur.gray("No Agent.md files found"),
      "",
      kleur.white("Changes will take effect on next AI interaction")
    ], 'green');
    
    console.log(reloadBox);
  }

  private handleGitCommand(args: string[]) {
    const subCommand = args[0] || 'status';
    
    switch (subCommand.toLowerCase()) {
      case 'status':
      case 's':
        this.executeNaturalLanguageCommand("Show enhanced git status with conflicts and operation info");
        break;
        
      case 'conflicts':
      case 'conflict':
        this.executeNaturalLanguageCommand("Detect and show all merge conflicts in the repository");
        break;
        
      case 'stash':
        if (args[1] === 'save' || args[1] === 'push') {
          const message = args.slice(2).join(' ') || 'Work in progress';
          this.executeNaturalLanguageCommand(`Stash current changes with message: "${message}"`);
        } else if (args[1] === 'pop') {
          this.executeNaturalLanguageCommand("Pop the most recent stash");
        } else if (args[1] === 'list') {
          this.executeNaturalLanguageCommand("List all git stashes");
        } else {
          this.executeNaturalLanguageCommand("Show git stash list");
        }
        break;
        
      case 'branch':
      case 'branches':
        if (args[1] === 'create' && args[2]) {
          this.executeNaturalLanguageCommand(`Create and switch to new branch: ${args[2]}`);
        } else {
          this.executeNaturalLanguageCommand("List all git branches");
        }
        break;
        
      case 'commit':
        const commitMessage = args.slice(1).join(' ');
        if (commitMessage) {
          this.executeNaturalLanguageCommand(`Generate and create a git commit with message: "${commitMessage}"`);
        } else {
          this.executeNaturalLanguageCommand("Generate an intelligent commit message based on staged changes and create commit");
        }
        break;
        
      case 'merge':
        if (args[1]) {
          this.executeNaturalLanguageCommand(`Merge branch ${args[1]} into current branch`);
        } else {
          this.showGitHelp();
        }
        break;
        
      case 'remote':
        this.executeNaturalLanguageCommand("Show git remote repositories with URLs");
        break;
        
      case 'help':
      default:
        this.showGitHelp();
    }
  }

  private handleGithubCommand(args: string[]) {
    const subCommand = args[0] || 'help';
    
    switch (subCommand.toLowerCase()) {
      case 'pr':
      case 'pull':
        if (args[1] === 'create') {
          const title = args.slice(2).join(' ');
          if (title) {
            this.executeNaturalLanguageCommand(`Create a GitHub pull request with title: "${title}"`);
          } else {
            this.executeNaturalLanguageCommand("Create a GitHub pull request with an intelligent title and description based on recent commits");
          }
        } else if (args[1] === 'list') {
          this.executeNaturalLanguageCommand("List all GitHub pull requests for this repository");
        } else {
          this.executeNaturalLanguageCommand("List GitHub pull requests");
        }
        break;
        
      case 'issue':
      case 'issues':
        if (args[1] === 'create') {
          const title = args.slice(2).join(' ');
          if (title) {
            this.executeNaturalLanguageCommand(`Create a GitHub issue with title: "${title}"`);
          } else {
            this.showGithubHelp();
          }
        } else if (args[1] === 'list') {
          this.executeNaturalLanguageCommand("List all open GitHub issues for this repository");
        } else {
          this.executeNaturalLanguageCommand("List GitHub issues");
        }
        break;
        
      case 'workflow':
      case 'actions':
        if (args[1] === 'list') {
          this.executeNaturalLanguageCommand("List all GitHub Actions workflows");
        } else if (args[1] === 'run' && args[2]) {
          this.executeNaturalLanguageCommand(`Run GitHub Actions workflow: ${args[2]}`);
        } else {
          this.executeNaturalLanguageCommand("Show GitHub Actions workflows status");
        }
        break;
        
      case 'repo':
        this.executeNaturalLanguageCommand("Show GitHub repository information and statistics");
        break;
        
      case 'help':
      default:
        this.showGithubHelp();
    }
  }

  private executeNaturalLanguageCommand(command: string) {
    // Execute the command as if it was typed naturally
    this.handleNaturalLanguage(command);
  }

  private showGitHelp() {
    const gitHelpBox = this.createBox([
      kleur.white("🔧 Git Commands"),
      "",
      kleur.blue("Basic Operations:"),
      kleur.cyan("  /git status") + kleur.gray("     - Enhanced git status with conflicts"),
      kleur.cyan("  /git conflicts") + kleur.gray("  - Detect and analyze merge conflicts"),
      kleur.cyan("  /git branch") + kleur.gray("     - List all branches"),
      kleur.cyan("  /git remote") + kleur.gray("     - Show remote repositories"),
      "",
      kleur.blue("Branch Management:"),
      kleur.cyan("  /git branch create <name>") + kleur.gray(" - Create new branch"),
      kleur.cyan("  /git merge <branch>") + kleur.gray("      - Merge branch"),
      "",
      kleur.blue("Stash Operations:"),
      kleur.cyan("  /git stash") + kleur.gray("        - List stashes"),
      kleur.cyan("  /git stash save <msg>") + kleur.gray(" - Save current changes"),
      kleur.cyan("  /git stash pop") + kleur.gray("      - Apply most recent stash"),
      "",
      kleur.blue("Commit Operations:"),
      kleur.cyan("  /git commit") + kleur.gray("       - Smart commit with generated message"),
      kleur.cyan("  /git commit <msg>") + kleur.gray("  - Commit with specific message"),
      "",
      kleur.white("💡 All commands use AI-powered git operations with approval gates")
    ], 'blue');
    
    console.log(gitHelpBox);
  }

  private showGithubHelp() {
    const githubHelpBox = this.createBox([
      kleur.white("🐙 GitHub Commands"),
      "",
      kleur.blue("Pull Requests:"),
      kleur.cyan("  /github pr") + kleur.gray("           - List pull requests"),
      kleur.cyan("  /github pr create") + kleur.gray("    - Create new PR"),
      kleur.cyan("  /github pr create <title>") + kleur.gray(" - Create PR with title"),
      "",
      kleur.blue("Issues:"),
      kleur.cyan("  /github issue") + kleur.gray("        - List open issues"),
      kleur.cyan("  /github issue create <title>") + kleur.gray(" - Create new issue"),
      "",
      kleur.blue("Actions & Workflows:"),
      kleur.cyan("  /github workflow") + kleur.gray("     - List workflows"),
      kleur.cyan("  /github workflow run <name>") + kleur.gray(" - Run workflow"),
      "",
      kleur.blue("Repository:"),
      kleur.cyan("  /github repo") + kleur.gray("         - Show repository info"),
      "",
      kleur.white("⚠️  Requires GitHub CLI (gh) to be installed and authenticated"),
      kleur.gray("   Install: https://cli.github.com/"),
      kleur.gray("   Login: gh auth login")
    ], 'blue');
    
    console.log(githubHelpBox);
  }

  private showHelp() {
    const helpBox = this.createBox([
      kleur.white("📚 Metis Code Help"),
      "",
      kleur.white("💬 Natural Language:"),
      kleur.gray("Just type what you want to do:"),
      kleur.yellow('  "Add error handling to the API endpoints"'),
      kleur.yellow('  "Create a React component for user profiles"'),
      kleur.yellow('  "Fix the TypeScript errors in the codebase"'),
      "",
      kleur.white("⚡ Slash Commands:"),
      kleur.cyan("  /init") + kleur.gray("        - Initialize project with Agent.md"),
      kleur.cyan("  /plan") + kleur.gray("        - Enter collaborative planning mode"),
      kleur.cyan("  /execute") + kleur.gray("      - Exit planning mode and start coding"),
      kleur.cyan("  /status") + kleur.gray("      - Show system status"),
      kleur.cyan("  /config") + kleur.gray("      - Show/manage configuration"),
      kleur.cyan("  /permissions") + kleur.gray(" - Show permission system info"),
      kleur.cyan("  /mode") + kleur.gray("        - Cycle permission modes"),
      kleur.cyan("  /clear") + kleur.gray("       - Clear conversation context"),
      kleur.cyan("  /compact") + kleur.gray("     - Compact/summarize session context"), 
      kleur.cyan("  /resume") + kleur.gray("      - Resume a previous session"),
      kleur.cyan("  /continue") + kleur.gray("     - Continue the last session"),
      kleur.cyan("  /sessions") + kleur.gray("    - List recent sessions"),
      kleur.cyan("  /memory") + kleur.gray("      - View memory system status"),
      kleur.cyan("  /reload") + kleur.gray("      - Refresh Agent.md files"),
      kleur.cyan("  /git") + kleur.gray("         - Git operations (type /git help)"),
      kleur.cyan("  /github") + kleur.gray("      - GitHub operations (type /github help)"),
      kleur.cyan("  /help") + kleur.gray("        - Show this help"),
      kleur.cyan("  /exit") + kleur.gray("        - Exit session"),
      "",
      kleur.white("🔒 Permission System:"),
      kleur.gray("• Three modes: Normal (ask), Auto-accept, Plan-only"),
      kleur.gray("• Use /mode to cycle or /mode <name> to set specific"),
      kleur.gray("• Current mode shows in prompt"),
      "",
      kleur.white("💡 Tips:"),
      kleur.gray("• Be specific about files or components to work with"),
      kleur.gray("• AI automatically reads and modifies files as needed"),
      kleur.gray("• Use Agent.md to customize AI behavior for your project")
    ], 'blue');
    
    console.log(helpBox);
  }

  private async enterPlanningMode() {
    const planBox = this.createBox([
      kleur.blue('Planning Mode Activated'),
      '',
      kleur.white('I will help you plan your project requirements and generate tasks.'),
      kleur.gray('This mode focuses on planning and will only generate Agent.md files.'),
      '',
      kleur.yellow('Tell me about your project or what you want to build:'),
      '',
      kleur.gray('When ready to start coding, use: /execute')
    ], 'blue');
    
    console.log(planBox);
    console.log();
    
    // Set the agent to planning mode
    this.agent.setPermissionMode('plan_only');
    
    // Show updated prompt
    this.rl.setPrompt(this.createPrompt());
    
    // Planning mode is now active - the agent will handle planning requests differently
  }

  private async exitPlanningMode() {
    const currentMode = this.agent.getPermissionManager().getCurrentMode();
    
    if (currentMode !== 'plan_only') {
      const notInPlanningBox = this.createBox([
        kleur.yellow('Not in Planning Mode'),
        '',
        kleur.gray('You are not currently in planning mode.'),
        kleur.white(`Current mode: ${this.agent.getPermissionModeDisplay()}`)
      ], 'yellow');
      
      console.log(notInPlanningBox);
      return;
    }
    
    const executeBox = this.createBox([
      kleur.green('Exiting Planning Mode'),
      '',
      kleur.white('Switching to execution mode.'),
      kleur.gray('You can now ask me to implement the planned features.'),
      '',
      kleur.cyan('Ready to start coding!')
    ], 'green');
    
    console.log(executeBox);
    console.log();
    
    // Switch back to normal mode
    this.agent.setPermissionMode('normal');
    
    // Show updated prompt
    this.rl.setPrompt(this.createPrompt());
    
    // No need to call AI here - just let the user make their next request
  }

  private prompt() {
    this.showPromptWithContainer();
  }
}